import { CALCULATORS } from "@/lib/calculators/data";
import { FEATURE_CONTENT_MAP } from "@/lib/features/content";
import { TOOL_CONTENT_MAP } from "@/lib/tools/content";
import { USE_CASE_CONTENT_MAP } from "@/lib/use-cases/content";
import { GLOSSARY_TERMS } from "@/lib/glossary/content";
import { IDEA_NICHE_MAP } from "@/lib/ideas/content";
import { comparePath, getRoundup, getSpoke } from "@/lib/compare";
import { LANGUAGE_PAGES } from "@/lib/languages/content";
import { getCaptionStyle } from "@/lib/caption-styles/content";
import { getDocPage } from "@/lib/docs/content";
import { getMarketingPage, getMarketingPages } from "@/lib/seo/pages";
import type { MarketingPage, MarketingPageKind } from "@/lib/seo/pages";

const FILL_ORDER: MarketingPageKind[] = ["feature", "useCase", "tool", "calculator"];

const compareRelated = (slug: string): string[] => {
  const spoke = getSpoke(slug);
  if (spoke) return [...spoke.content.relatedSpokes.map(comparePath), spoke.content.featurePath];
  return getRoundup(slug)?.relatedSpokes.map(comparePath) ?? [];
};

const declaredRelated = (page: MarketingPage): string[] => {
  if (page.kind === "feature") return FEATURE_CONTENT_MAP[page.slug]?.related ?? [];
  if (page.kind === "tool") return TOOL_CONTENT_MAP[page.slug]?.related ?? [];
  if (page.kind === "useCase") return USE_CASE_CONTENT_MAP[page.slug]?.related ?? [];
  if (page.kind === "glossary") return GLOSSARY_TERMS[page.slug]?.related ?? [];
  if (page.kind === "idea") return IDEA_NICHE_MAP[page.slug]?.related ?? [];
  if (page.kind === "compare") return compareRelated(page.slug);
  if (page.kind === "language") return LANGUAGE_PAGES[page.slug]?.related ?? [];
  if (page.kind === "captionStyle") return getCaptionStyle(page.slug)?.related ?? [];
  if (page.kind === "doc") return getDocPage(page.slug)?.related ?? [];
  return CALCULATORS.find((calculator) => calculator.slug === page.slug)?.related ?? [];
};

export const getRelatedPages = (path: string, count = 6): MarketingPage[] => {
  const page = getMarketingPage(path);
  const visible = getMarketingPages();
  const chosen: MarketingPage[] = [];
  const seen = new Set<string>([path]);

  const add = (candidate: MarketingPage | undefined) => {
    if (!candidate || chosen.length >= count || seen.has(candidate.path)) return;
    if (!visible.some((entry) => entry.path === candidate.path)) return;
    seen.add(candidate.path);
    chosen.push(candidate);
  };

  if (page) {
    declaredRelated(page).forEach((related) => add(getMarketingPage(related)));
    getMarketingPages(page.kind).forEach(add);
  }

  FILL_ORDER.forEach((kind) => getMarketingPages(kind).forEach(add));

  return chosen;
};
